import React, { useEffect, useRef, useState } from 'react';
import {
  Box, LinearProgress, Typography, Chip, Collapse, IconButton,
  List, ListItem, ListItemIcon, ListItemText
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import BlockIcon from '@mui/icons-material/Block';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import DownloadIcon from '@mui/icons-material/Download';
import CalculateIcon from '@mui/icons-material/Calculate';
import {
  strategyScanService, StrategyScanJob, PerSymbolStatusEntry, PerSymbolScanStatus
} from '../../services/strategyScan.service';

interface Props {
  strategyId: number;
  jobId: number;
  onDone?: (job: StrategyScanJob) => void;
  pollMs?: number;
}

const TERMINAL: Array<StrategyScanJob['status']> = ['COMPLETED', 'FAILED'];

function statusIcon(status: PerSymbolScanStatus) {
  switch (status) {
    case 'SIGNAL': return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />;
    case 'CLEAN': return <HorizontalRuleIcon sx={{ color: 'text.disabled', fontSize: 18 }} />;
    case 'SKIPPED_INSUFFICIENT_DATA': return <BlockIcon sx={{ color: 'warning.main', fontSize: 18 }} />;
    case 'ERROR': return <ErrorIcon sx={{ color: 'error.main', fontSize: 18 }} />;
    case 'FETCHING': return <DownloadIcon sx={{ color: 'info.main', fontSize: 18 }} />;
    case 'EVALUATING': return <CalculateIcon sx={{ color: 'info.main', fontSize: 18 }} />;
    default: return <HourglassBottomIcon sx={{ color: 'text.disabled', fontSize: 18 }} />;
  }
}

function statusLabel(e: PerSymbolStatusEntry): string {
  // R115 Stage 3 — surface the per-symbol diagnostic message in every status
  // label so users can see WHY a symbol returned CLEAN (insufficient warmup,
  // position already established, etc.) AND, for scheduled SIGNAL rows,
  // whether the Route to Dashboard / Telegram switches were honored.
  if (e.status === 'SIGNAL') {
    const head = e.signalAction ? `SIGNAL — ${e.signalAction}` : 'SIGNAL';
    return e.message ? `${head} · ${e.message}` : head;
  }
  if (e.status === 'SKIPPED_INSUFFICIENT_DATA') return e.message || 'Insufficient data';
  if (e.status === 'ERROR') return e.message ? `Error — ${e.message}` : 'Error';
  if (e.status === 'CLEAN') return e.message ? `Clean · ${e.message}` : 'Clean (no signal)';
  if (e.status === 'FETCHING') return 'Fetching candles…';
  if (e.status === 'EVALUATING') return 'Evaluating…';
  return 'Queued';
}

function phaseLabel(phase?: string) {
  switch (phase) {
    case 'QUEUED': return 'Queued';
    case 'FETCHING_HISTORY': return 'Fetching candles';
    case 'EVALUATING': return 'Evaluating';
    case 'PERSISTING': return 'Persisting signal';
    case 'DONE': return 'Done';
    default: return phase || '';
  }
}

const ScanProgress: React.FC<Props> = ({ strategyId, jobId, onDone, pollMs = 1500 }) => {
  const [job, setJob] = useState<StrategyScanJob | null>(null);
  const [expanded, setExpanded] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const stoppedRef = useRef(false);
  const onDoneRef = useRef(onDone);
  const firedForJobIdRef = useRef<number | null>(null);

  // Keep onDoneRef current without re-running the polling effect
  useEffect(() => { onDoneRef.current = onDone; }, [onDone]);

  useEffect(() => {
    stoppedRef.current = false;
    let timer: any;

    const poll = async () => {
      if (stoppedRef.current) return;
      try {
        const j = await strategyScanService.getScanJob(strategyId, jobId);
        if (stoppedRef.current) return;
        setJob(j);
        setLastRefreshed(new Date());
        if (j && TERMINAL.includes(j.status)) {
          stoppedRef.current = true;
          if (firedForJobIdRef.current !== jobId) {
            firedForJobIdRef.current = jobId;
            onDoneRef.current?.(j);
          }
          return;
        }
      } catch {
        // transient — keep polling
      }
      timer = setTimeout(poll, pollMs);
    };

    poll();
    return () => {
      stoppedRef.current = true;
      if (timer) clearTimeout(timer);
    };
  }, [strategyId, jobId, pollMs]);

  if (!job) {
    return (
      <Box sx={{ my: 1 }}>
        <LinearProgress />
        <Typography variant="caption" color="text.secondary">Starting scan…</Typography>
      </Box>
    );
  }

  const total = job.totalSymbols || 0;
  const processed = job.processedSymbols || 0;
  const pct = total > 0 ? Math.min(100, (processed / total) * 100) : (TERMINAL.includes(job.status) ? 100 : 0);
  const perSymbol = job.perSymbolStatus || [];
  const isDone = TERMINAL.includes(job.status);
  const signalCount = (job.signalsFound || 0);

  return (
    <Box sx={{ my: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" fontWeight={700}>
            {isDone
              ? (job.status === 'COMPLETED' ? 'Scan complete' : 'Scan failed')
              : `Scanning ${processed} / ${total}${job.currentSymbol ? ` — ${job.currentSymbol}` : ''}`}
          </Typography>
          {!isDone && (
            <Chip label={phaseLabel(job.phase)} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
          )}
          {isDone && (
            <Chip
              label={`${signalCount} signal${signalCount === 1 ? '' : 's'}`}
              size="small"
              color={signalCount > 0 ? 'success' : 'default'}
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {lastRefreshed && (
            <Typography variant="caption" color="text.secondary">
              {lastRefreshed.toLocaleTimeString()}
            </Typography>
          )}
          <IconButton size="small" onClick={() => setExpanded(e => !e)}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>
      <LinearProgress
        variant={isDone || total > 0 ? 'determinate' : 'indeterminate'}
        value={pct}
        color={job.status === 'FAILED' ? 'error' : (job.status === 'COMPLETED' ? 'success' : 'primary')}
        sx={{ height: 6, borderRadius: 1 }}
      />
      {job.status === 'FAILED' && job.error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {job.error}
        </Typography>
      )}
      <Collapse in={expanded} unmountOnExit>
        <List dense sx={{ mt: 1, maxHeight: 260, overflow: 'auto' }}>
          {perSymbol.map((e, i) => (
            <ListItem key={`${e.symbol}-${e.exchange}-${i}`} sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>{statusIcon(e.status)}</ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    <strong>{e.symbol}</strong>
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                      ({e.exchange})
                    </Typography>
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {statusLabel(e)}
                  </Typography>
                }
              />
            </ListItem>
          ))}
          {perSymbol.length === 0 && (
            <ListItem><ListItemText secondary="Waiting for per-symbol status…" /></ListItem>
          )}
        </List>
      </Collapse>
    </Box>
  );
};

export default ScanProgress;
